import React from 'react';
import { BarChart3Icon, FileTextIcon, CheckCircleIcon, AlertTriangleIcon, ClockIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, FileSignatureIcon, CalendarIcon, MinusSquareIcon, TrendingUpIcon, TrendingDownIcon, AlertCircleIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
interface APDashboardProps {
  invoices: any[];
  setCurrentView: (view: string) => void;
  locale?: string;
}
export function APDashboard({
  invoices,
  setCurrentView,
  locale = 'en-US'
}: APDashboardProps) {
  // Calculate invoice statistics
  const totalInvoices = invoices.length;
  const pendingApproval = invoices.filter(inv => inv.status === 'Pending Approval').length;
  const approved = invoices.filter(inv => inv.status === 'Approved').length;
  const paid = invoices.filter(inv => inv.status === 'Paid').length;
  // Calculate total invoice amount
  const totalInvoiceAmount = invoices.reduce((sum, inv) => sum + inv.total, 0);
  // Sample data for credit notes
  const creditNotes = [{
    id: 'CN-2023-001',
    creditNoteNumber: 'CN-2023-001',
    vendor: 'Office Depot',
    creditNoteDate: '2023-10-15',
    amount: 750.25,
    currency: 'USD',
    status: 'Draft'
  }, {
    id: 'CN-2023-002',
    creditNoteNumber: 'CN-2023-002',
    vendor: 'Dell Technologies',
    creditNoteDate: '2023-10-12',
    amount: 430.0,
    currency: 'USD',
    status: 'Approved'
  }];
  // Sample data for RCTIs
  const rctis = [{
    id: 'RCTI-2023-001',
    rctiNumber: 'RCTI-2023-001',
    vendor: 'Supplier Co.',
    rctiDate: '2023-10-10',
    amount: 1200.5,
    currency: 'USD',
    status: 'Pending'
  }, {
    id: 'RCTI-2023-002',
    rctiNumber: 'RCTI-2023-002',
    vendor: 'Service Provider Ltd',
    rctiDate: '2023-10-08',
    amount: 850.75,
    currency: 'USD',
    status: 'Posted'
  }];
  // Calculate credit note statistics
  const totalCreditNotes = creditNotes.length;
  const totalCreditNoteAmount = creditNotes.reduce((sum, cn) => sum + cn.amount, 0);
  // Calculate RCTI statistics
  const totalRCTIs = rctis.length;
  const totalRCTIAmount = rctis.reduce((sum, rcti) => sum + rcti.amount, 0);
  // Calculate overall AP statistics
  const totalDocuments = totalInvoices + totalCreditNotes + totalRCTIs;
  const totalDebits = totalInvoiceAmount + totalRCTIAmount;
  const totalCredits = totalCreditNoteAmount;
  const netAmount = totalDebits - totalCredits;
  // Status counts
  const documentStatusCounts = {
    'Pending Approval': pendingApproval + creditNotes.filter(cn => cn.status === 'Pending').length + rctis.filter(r => r.status === 'Pending').length,
    Approved: approved + creditNotes.filter(cn => cn.status === 'Approved').length + rctis.filter(r => r.status === 'Approved').length,
    'Paid/Posted': paid + creditNotes.filter(cn => cn.status === 'Posted').length + rctis.filter(r => r.status === 'Posted').length,
    Draft: invoices.filter(inv => inv.status === 'Draft').length + creditNotes.filter(cn => cn.status === 'Draft').length + rctis.filter(r => r.status === 'Draft').length,
    'Failed Compliance': 3 // Sample data
  };
  // Get recent documents (combine and sort all document types)
  const recentDocuments = [...invoices.map(inv => ({
    ...inv,
    type: 'Invoice',
    number: inv.invoiceNumber,
    date: inv.invoiceDate,
    amount: inv.total
  })), ...creditNotes.map(cn => ({
    ...cn,
    type: 'Credit Note',
    number: cn.creditNoteNumber,
    date: cn.creditNoteDate
  })), ...rctis.map(rcti => ({
    ...rcti,
    type: 'RCTI',
    number: rcti.rctiNumber,
    date: rcti.rctiDate
  }))].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
  return <div data-id="element-335">
      <div className="mb-8 flex justify-between items-center" data-id="element-336">
        <div data-id="element-337">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-338">AP Dashboard</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-339">
            Overview of invoices, credit notes, and RCTIs
          </p>
        </div>
        <div className="flex space-x-2" data-id="element-340">
          <button onClick={() => setCurrentView('invoice-entry')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-341">
            <FileTextIcon className="h-4 w-4 mr-1" data-id="element-342" />
            New Invoice
          </button>
          <button onClick={() => setCurrentView('credit-note-entry')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-343">
            <MinusSquareIcon className="h-4 w-4 mr-1" data-id="element-344" />
            New Credit Note
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8" data-id="element-345">
        {/* Total Documents */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-346">
          <div className="px-4 py-5 sm:p-6" data-id="element-347">
            <div className="flex items-center" data-id="element-348">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-349">
                <FileTextIcon className="h-6 w-6 text-white" data-id="element-350" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-351">
                <dl data-id="element-352">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-353">
                    Total Documents
                  </dt>
                  <dd className="flex items-baseline" data-id="element-354">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-355">
                      {totalDocuments}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-356">
            <div className="text-sm" data-id="element-357">
              <a href="#" onClick={() => setCurrentView('invoice-list')} className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-358">
                View all documents
              </a>
            </div>
          </div>
        </div>

        {/* Net Amount */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-359">
          <div className="px-4 py-5 sm:p-6" data-id="element-360">
            <div className="flex items-center" data-id="element-361">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-362">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-363" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-364">
                <dl data-id="element-365">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-366">
                    Net Amount
                  </dt>
                  <dd className="flex items-baseline" data-id="element-367">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-368">
                      {formatCurrency(netAmount, 'USD', locale)}
                    </div>
                    {netAmount > 0 ? <span className="ml-2 flex items-baseline text-sm font-semibold text-green-600" data-id="element-369">
                        <TrendingUpIcon className="self-center flex-shrink-0 h-4 w-4 text-green-500" data-id="element-370" />
                        <span className="sr-only" data-id="element-371">Increased by</span>
                      </span> : <span className="ml-2 flex items-baseline text-sm font-semibold text-red-600" data-id="element-372">
                        <TrendingDownIcon className="self-center flex-shrink-0 h-4 w-4 text-red-500" data-id="element-373" />
                        <span className="sr-only" data-id="element-374">Decreased by</span>
                      </span>}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-375">
            <div className="text-sm flex justify-between" data-id="element-376">
              <span className="text-gray-500" data-id="element-377">
                Debits: {formatCurrency(totalDebits, 'USD', locale)}
              </span>
              <span className="text-gray-500" data-id="element-378">
                Credits: {formatCurrency(totalCredits, 'USD', locale)}
              </span>
            </div>
          </div>
        </div>

        {/* Pending Approval */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-379">
          <div className="px-4 py-5 sm:p-6" data-id="element-380">
            <div className="flex items-center" data-id="element-381">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3" data-id="element-382">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-383" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-384">
                <dl data-id="element-385">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-386">
                    Pending Approval
                  </dt>
                  <dd className="flex items-baseline" data-id="element-387">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-388">
                      {documentStatusCounts['Pending Approval']}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-389">
            <div className="text-sm" data-id="element-390">
              <a href="#" className="font-medium text-yellow-600 hover:text-yellow-500" data-id="element-391">
                Review pending documents
              </a>
            </div>
          </div>
        </div>

        {/* Failed Compliance */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-392">
          <div className="px-4 py-5 sm:p-6" data-id="element-393">
            <div className="flex items-center" data-id="element-394">
              <div className="flex-shrink-0 bg-red-500 rounded-md p-3" data-id="element-395">
                <AlertCircleIcon className="h-6 w-6 text-white" data-id="element-396" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-397">
                <dl data-id="element-398">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-399">
                    Failed Compliance
                  </dt>
                  <dd className="flex items-baseline" data-id="element-400">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-401">
                      {documentStatusCounts['Failed Compliance']}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-402">
            <div className="text-sm" data-id="element-403">
              <a href="#" className="font-medium text-red-600 hover:text-red-500" data-id="element-404">
                Resolve compliance issues
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Document Type Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-405">
        {/* Invoices */}
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-406">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-407">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-408">
              Invoices
            </h3>
            <button onClick={() => setCurrentView('invoice-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-409">
              View all
            </button>
          </div>
          <div className="p-6" data-id="element-410">
            <div className="flex justify-between mb-4" data-id="element-411">
              <div className="text-center" data-id="element-412">
                <div className="text-2xl font-bold text-gray-900" data-id="element-413">
                  {totalInvoices}
                </div>
                <div className="text-sm text-gray-500" data-id="element-414">Total</div>
              </div>
              <div className="text-center" data-id="element-415">
                <div className="text-2xl font-bold text-gray-900" data-id="element-416">
                  {formatCurrency(totalInvoiceAmount, 'USD', locale)}
                </div>
                <div className="text-sm text-gray-500" data-id="element-417">Amount</div>
              </div>
              <div className="text-center" data-id="element-418">
                <div className="text-2xl font-bold text-yellow-500" data-id="element-419">
                  {pendingApproval}
                </div>
                <div className="text-sm text-gray-500" data-id="element-420">Pending</div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-421">
              <div className="bg-indigo-600 h-2.5 rounded-full" style={{
              width: `${approved / totalInvoices * 100}%`
            }} data-id="element-422"></div>
            </div>
            <div className="space-y-4 mt-4" data-id="element-423">
              <div className="flex items-center justify-between" data-id="element-424">
                <div className="flex items-center" data-id="element-425">
                  <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" data-id="element-426"></div>
                  <span className="text-sm text-gray-500" data-id="element-427">Pending</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-428">
                  {pendingApproval}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-429">
                <div className="flex items-center" data-id="element-430">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-431"></div>
                  <span className="text-sm text-gray-500" data-id="element-432">Approved</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-433">
                  {approved}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-434">
                <div className="flex items-center" data-id="element-435">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" data-id="element-436"></div>
                  <span className="text-sm text-gray-500" data-id="element-437">Paid</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-438">
                  {paid}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Credit Notes */}
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-439">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-440">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-441">
              Credit Notes
            </h3>
            <button onClick={() => setCurrentView('credit-note-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-442">
              View all
            </button>
          </div>
          <div className="p-6" data-id="element-443">
            <div className="flex justify-between mb-4" data-id="element-444">
              <div className="text-center" data-id="element-445">
                <div className="text-2xl font-bold text-gray-900" data-id="element-446">
                  {totalCreditNotes}
                </div>
                <div className="text-sm text-gray-500" data-id="element-447">Total</div>
              </div>
              <div className="text-center" data-id="element-448">
                <div className="text-2xl font-bold text-gray-900" data-id="element-449">
                  {formatCurrency(totalCreditNoteAmount, 'USD', locale)}
                </div>
                <div className="text-sm text-gray-500" data-id="element-450">Amount</div>
              </div>
              <div className="text-center" data-id="element-451">
                <div className="text-2xl font-bold text-green-500" data-id="element-452">
                  {creditNotes.filter(cn => cn.status === 'Approved').length}
                </div>
                <div className="text-sm text-gray-500" data-id="element-453">Approved</div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-454">
              <div className="bg-green-600 h-2.5 rounded-full" style={{
              width: `${creditNotes.filter(cn => cn.status === 'Approved').length / totalCreditNotes * 100}%`
            }} data-id="element-455"></div>
            </div>
            <div className="space-y-4 mt-4" data-id="element-456">
              <div className="flex items-center justify-between" data-id="element-457">
                <div className="flex items-center" data-id="element-458">
                  <div className="w-3 h-3 rounded-full bg-gray-300 mr-2" data-id="element-459"></div>
                  <span className="text-sm text-gray-500" data-id="element-460">Draft</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-461">
                  {creditNotes.filter(cn => cn.status === 'Draft').length}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-462">
                <div className="flex items-center" data-id="element-463">
                  <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" data-id="element-464"></div>
                  <span className="text-sm text-gray-500" data-id="element-465">Pending</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-466">
                  {creditNotes.filter(cn => cn.status === 'Pending').length}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-467">
                <div className="flex items-center" data-id="element-468">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-469"></div>
                  <span className="text-sm text-gray-500" data-id="element-470">Approved</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-471">
                  {creditNotes.filter(cn => cn.status === 'Approved').length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* RCTIs */}
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-472">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-473">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-474">
              RCTIs
            </h3>
            <button onClick={() => setCurrentView('rcti-management')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-475">
              View all
            </button>
          </div>
          <div className="p-6" data-id="element-476">
            <div className="flex justify-between mb-4" data-id="element-477">
              <div className="text-center" data-id="element-478">
                <div className="text-2xl font-bold text-gray-900" data-id="element-479">
                  {totalRCTIs}
                </div>
                <div className="text-sm text-gray-500" data-id="element-480">Total</div>
              </div>
              <div className="text-center" data-id="element-481">
                <div className="text-2xl font-bold text-gray-900" data-id="element-482">
                  {formatCurrency(totalRCTIAmount, 'USD', locale)}
                </div>
                <div className="text-sm text-gray-500" data-id="element-483">Amount</div>
              </div>
              <div className="text-center" data-id="element-484">
                <div className="text-2xl font-bold text-blue-500" data-id="element-485">
                  {rctis.filter(r => r.status === 'Posted').length}
                </div>
                <div className="text-sm text-gray-500" data-id="element-486">Posted</div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-487">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{
              width: `${rctis.filter(r => r.status === 'Posted').length / totalRCTIs * 100}%`
            }} data-id="element-488"></div>
            </div>
            <div className="space-y-4 mt-4" data-id="element-489">
              <div className="flex items-center justify-between" data-id="element-490">
                <div className="flex items-center" data-id="element-491">
                  <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" data-id="element-492"></div>
                  <span className="text-sm text-gray-500" data-id="element-493">Pending</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-494">
                  {rctis.filter(r => r.status === 'Pending').length}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-495">
                <div className="flex items-center" data-id="element-496">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-497"></div>
                  <span className="text-sm text-gray-500" data-id="element-498">Approved</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-499">
                  {rctis.filter(r => r.status === 'Approved').length}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-500">
                <div className="flex items-center" data-id="element-501">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" data-id="element-502"></div>
                  <span className="text-sm text-gray-500" data-id="element-503">Posted</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-504">
                  {rctis.filter(r => r.status === 'Posted').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Documents */}
      <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-505">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-506">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-507">
            Recent Documents
          </h3>
          <div className="flex space-x-2" data-id="element-508">
            <button onClick={() => setCurrentView('invoice-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-509">
              Invoices
            </button>
            <button onClick={() => setCurrentView('credit-note-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-510">
              Credit Notes
            </button>
            <button onClick={() => setCurrentView('rcti-management')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-511">
              RCTIs
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-512">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-513">
            <thead className="bg-gray-50" data-id="element-514">
              <tr data-id="element-515">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-516">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-517">
                  Number
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-518">
                  Vendor
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-519">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-520">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-521">
                  Status
                </th>
                <th scope="col" className="relative px-6 py-3" data-id="element-522">
                  <span className="sr-only" data-id="element-523">View</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-524">
              {recentDocuments.length > 0 ? recentDocuments.map(doc => <tr key={doc.id} className="hover:bg-gray-50" data-id="element-525">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-526">
                      <div className="flex items-center" data-id="element-527">
                        {doc.type === 'Invoice' ? <FileTextIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-528" /> : doc.type === 'Credit Note' ? <MinusSquareIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-529" /> : <FileSignatureIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-530" />}
                        <span className="text-sm text-gray-900" data-id="element-531">
                          {doc.type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-532">
                      {doc.number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-533">
                      {doc.vendor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-534">
                      {new Date(doc.date).toLocaleDateString(locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-535">
                      {formatCurrency(doc.amount, doc.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-536">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${doc.status === 'Approved' ? 'bg-green-100 text-green-800' : doc.status === 'Pending Approval' || doc.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : doc.status === 'Draft' ? 'bg-gray-100 text-gray-800' : doc.status === 'Paid' || doc.status === 'Posted' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`} data-id="element-537">
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-538">
                      <a href="#" onClick={() => {
                  if (doc.type === 'Invoice') {
                    setCurrentView('invoice-list');
                  } else if (doc.type === 'Credit Note') {
                    setCurrentView('credit-note-list');
                  } else {
                    setCurrentView('rcti-management');
                  }
                }} className="text-indigo-600 hover:text-indigo-900" data-id="element-539">
                        View
                      </a>
                    </td>
                  </tr>) : <tr data-id="element-540">
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-541">
                    No documents found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}